/**
 * THE SIGNATURE EMAIL — the ONE copy of the tokens, the shipped wording and the
 * substitution that turns one into the other.
 *
 * The same standing as `src/lib/credit-notes.ts` and
 * `src/lib/company-projects.ts`: the Settings screen that EDITS this wording and
 * the send dialog that SENDS it both come through here, so the two cannot spell
 * a token differently, ship different defaults, or disagree about what happens
 * when a token has nothing to resolve to.
 *
 * ⛔ **NOTHING HERE FETCHES OR WRITES. IT DECIDES AND IT FORMATS.** There are no
 * hooks, no `api` import and no request builders, and none may be added — the
 * pages own their own requests and their own draft state. The wire half is
 * `api.docusignTemplates` / `api.setDocusignTemplates` over
 * `GET | PATCH /settings/docusign-templates`, and {@link DocuSignEmailTemplates}
 * in `@/lib/types` is its shape.
 *
 * ## 🔴 THE ONE RULE THE WHOLE FILE EXISTS FOR
 *
 * **A TOKEN IS A PROMISE THAT IT RESOLVES ON EVERY SEND, AND AN UNRESOLVED ONE
 * MUST NEVER REACH A CUSTOMER.** `{{signerFirstName}}` printed raw in somebody's
 * inbox is the failure this module is built to make structurally impossible —
 * not unlikely, impossible. {@link fillTemplate} states the invariant, and the
 * way it holds is that **the placeholder SHAPE is the gate, never the token
 * list**: anything of the form `{{…}}` that cannot be resolved is removed,
 * including a token this build has never heard of.
 *
 * ⚠️ **The second half of that rule is the one that is easy to miss: removing a
 * token leaves a SEAM.** Naive substitution of an empty string turns
 * `Hi {{signerFirstName}},` into `Hi ,` — a comma with nothing in front of it,
 * which reads as a broken app rather than as a missing name, and is worse than
 * either the raw token or a generic greeting. See {@link fillTemplate} for the
 * two mechanisms that close it and why one alone was not enough.
 *
 * ## ⛔ THE NO-HELPER-TEXT PROHIBITION, AND WHAT THIS FILE IS
 *
 * This app forbids helper text outright. **Nothing in this module is helper
 * text**: the wording constants are the CONTENT of an email a customer reads,
 * and {@link DOCUSIGN_TOKEN_LABELS} is a set of LABELS naming the four tokens on
 * the Settings screen. A label is not prose attached to a control and needs no
 * exception. Tagged here because a sweep reading only the strings cannot tell a
 * label from a hint.
 *
 * ## ⚠️ A NAME COLLISION THAT IS ALREADY IN THE IMPORT BLOCK
 *
 * **`DocuSignTemplate` in `@/lib/types` is a DIFFERENT THING** — it is one of
 * DocuSign's own stored document templates, listed by
 * `api.listDocusignTemplates`, and it is what an envelope can be raised FROM.
 * This module is about the SUBJECT LINE AND MESSAGE BODY of the email that
 * carries an envelope, whichever way that envelope was raised. Two unrelated
 * facts one word apart, and the endpoint's own path (`docusign-templates`) keeps
 * the ambiguity alive, so every name here says **email** where it can.
 */

/* -------------------------------------------------------------------------
 * The tokens
 * --------------------------------------------------------------------- */

/**
 * The four substitutions a signature email may carry. Owner's decision,
 * 2026-09-17: all four are offered, and there are no others.
 *
 * ⚠️ **THIS LIST IS THE VOCABULARY, NOT THE GUARD.** A template naming something
 * absent from it is not refused and does not print raw — {@link fillTemplate}
 * removes it, because an administrator's typo must not reach a customer either
 * way. So adding a member here ADDS a capability; it does not close a hole.
 *
 * ⚠️ **Every member needs an entry in {@link DOCUSIGN_TOKEN_LABELS} and in the
 * fallback table inside {@link fillTemplate}**, both of which are exhaustive
 * `Record`s over this union — so a fifth token is a compile error until somebody
 * has decided what it is called on screen and what it degrades to. That is
 * deliberate: the degradation is the decision most easily forgotten, and it is
 * the one a customer sees.
 */
export const DOCUSIGN_TOKENS = [
  'signerFirstName',
  'quoteNumber',
  'companyName',
  'senderName',
] as const;

/** One of the four substitutions a signature email may carry. */
export type DocuSignToken = (typeof DOCUSIGN_TOKENS)[number];

/**
 * What each token is called on the Settings screen.
 *
 * **Labels, not helper text.** They name the four things a template can say;
 * they explain no control.
 *
 * ⚠️ **`senderName` is the SENDER'S name, not necessarily the reader's.** An
 * administrator editing this wording is usually not the person who will press
 * Send, so "Your name" would be wrong for exactly the person configuring it.
 */
export const DOCUSIGN_TOKEN_LABELS: Record<DocuSignToken, string> = {
  signerFirstName: 'Signer’s first name',
  quoteNumber: 'Quote number',
  companyName: 'Company name',
  senderName: 'Sender’s name',
};

/**
 * A token as it is written INTO a template — `{{quoteNumber}}`.
 *
 * ⚠️ **The one place the braces are spelled**, so a screen listing the tokens,
 * an insert-at-cursor control and the shipped defaults below cannot disagree
 * with the matcher about the syntax. Hand-building the braces at a call site is
 * how a template ends up holding `{{ quoteNumber }}`-with-a-typo that silently
 * resolves to nothing.
 *
 * Double braces are the owner's decision: ordinary prose carrying a single brace
 * cannot collide with one.
 */
export function tokenPlaceholder(token: DocuSignToken): string {
  return `{{${token}}}`;
}

/** True for a name this build knows how to resolve. */
export function isDocuSignToken(name: string): name is DocuSignToken {
  return (DOCUSIGN_TOKENS as readonly string[]).includes(name);
}

/* -------------------------------------------------------------------------
 * The shipped wording
 * --------------------------------------------------------------------- */

/**
 * The subject line a deployment starts with, and what it falls back to when an
 * administrator clears the configured one.
 *
 * ⚠️ **`{{quoteNumber}}` CAN BE EMPTY AND THE SUBJECT STILL HAS TO READ.** A
 * quote number is allocated by the server from a ledger, so a quote can exist
 * without one — this is not a hypothetical branch. With nothing to substitute
 * the subject degrades to `Complete with DocuSign: SEED Quote`, which is a
 * sentence; see {@link fillTemplate} for why it is not
 * `…SEED Quote ` with a trailing space.
 */
export const DEFAULT_SIGNATURE_SUBJECT = `Complete with DocuSign: SEED Quote ${tokenPlaceholder(
  'quoteNumber',
)}`;

/**
 * The message body a deployment starts with. **The owner's own wording**, with
 * the greeting tokenised and nothing else changed.
 *
 * ⚠️ **BUILT BY `join('\n')` RATHER THAN AS A MULTI-LINE TEMPLATE LITERAL, AND
 * THAT IS NOT A STYLE CHOICE.** A template literal spanning lines embeds *the
 * source file's own line endings* into the string, and this repository has mixed
 * endings with no `.gitattributes` and no rule — so the same constant would
 * carry `\n` in one checkout and `\r\n` in another, and the difference would
 * travel through the API into a customer's inbox. Joining explicitly makes the
 * value a property of this declaration rather than of whoever last saved the
 * file.
 *
 * ⚠️ **The sign-off is a literal, not {@link tokenPlaceholder}(`senderName`).**
 * The owner's wording signs off as the organisation rather than as a person, and
 * substituting the individual who pressed Send would change who the customer
 * believes they are dealing with. An administrator who wants the sender named
 * can put the token there themselves — which is the entire point of the feature
 * — but the shipped default must say what the owner wrote.
 */
export const DEFAULT_SIGNATURE_MESSAGE = [
  `Hi ${tokenPlaceholder('signerFirstName')},`,
  '',
  'I hope you are doing well.',
  '',
  'Please review and sign the attached quote at your earliest convenience.',
  '',
  'If you have any specific invoice instructions or a purchase order number to include, please do feel free to mention these in the Invoice Instructions (if any) section.',
  '',
  'If you have any questions or require further clarification, please do not hesitate to reach out.',
  '',
  'Best regards,',
  'SEED Global Education',
].join('\n');

/* -------------------------------------------------------------------------
 * Resolving what the server stored
 * --------------------------------------------------------------------- */

/**
 * The configured wording, or the shipped default when there is none.
 *
 * ⚠️ **`null` FROM THE SERVER MEANS "USE THE BUILT-IN DEFAULT"** — the same
 * semantics `quoteTerms` has, and the state every deployment is in until an
 * administrator first saves. It is the commonest case in the system rather than
 * the exceptional one.
 *
 * ⛔ **THE TEST IS POSITIVE — "is this a usable string" — AND NEVER `=== null`
 * OR `??`.** Four different values have to land on the default and only one of
 * them is `null`:
 *
 * ```
 * null        nobody has configured one            → default
 * undefined   a server that predates the key       → default
 * ''          an empty box the server did not      → default
 *             normalise
 * '   '       whitespace somebody typed and left   → default
 * ```
 *
 * `stored ?? fallback` gets the first two and silently sends the last two as the
 * subject line of a customer's email — an empty subject, or one made of spaces.
 * A positive test cannot be wrong about a value nobody anticipated, which is why
 * it is the shape used here and at every token in {@link fillTemplate}.
 *
 * ⚠️ **Returns `stored` UNTRIMMED.** The trim decides EMPTINESS and nothing
 * else: a leading blank line in a message body is an administrator's layout, and
 * quietly reformatting what somebody typed is a different feature from falling
 * back when they typed nothing.
 */
export function resolveTemplate(stored: string | null | undefined, fallback: string): string {
  return typeof stored === 'string' && stored.trim() !== '' ? stored : fallback;
}

/* -------------------------------------------------------------------------
 * Who the signer is
 * --------------------------------------------------------------------- */

/**
 * The signer's first name — the contact's stored one when the signer IS a
 * contact, otherwise the first word of whatever was typed. Owner's decision.
 *
 * Returns **`null` when neither is available**, which is an honest answer and
 * not a failure: the signer may have been typed as an address with no name at
 * all. {@link fillTemplate} owns what a greeting does with that, because the
 * never-leave-a-hole rule belongs to substitution rather than to identity.
 *
 * ⚠️ **The stored name WINS, and that is the decision rather than a preference.**
 * A contact's `firstName` is a field somebody curated; the typed name is a box
 * on a dialog that routinely holds `Accounts Payable` or a whole address. Where
 * both exist the curated one is the one to greet a customer by.
 *
 * ⚠️ **"The first word" takes a TITLE when there is one** — `Dr. Anjali Sharma`
 * greets as `Dr.`. That is the owner's rule applied literally, and it is
 * deliberately not softened with a list of titles to strip: such a list is
 * English-shaped, and most of this customer base is in India, MENA and South
 * East Asia, where it would be wrong more often than the wart it fixes. The way
 * out is the contact's own `firstName`, which is the branch above.
 *
 * ⚠️ `contactFirstName` is guarded with `typeof` rather than `!== null` because
 * a contact record legitimately arrives without the key at all, and `.trim()` on
 * `undefined` throws inside a render.
 */
export function signerFirstName(signerName: string, contactFirstName: string | null): string | null {
  const stored = typeof contactFirstName === 'string' ? contactFirstName.trim() : '';
  if (stored !== '') return stored;

  const typed = typeof signerName === 'string' ? signerName.trim() : '';
  if (typed === '') return null;

  const [first] = typed.split(/\s+/);
  return first !== undefined && first !== '' ? first : null;
}

/* -------------------------------------------------------------------------
 * Substitution
 * --------------------------------------------------------------------- */

/** What a caller knows about the four tokens. A missing key is an absent value. */
export type DocuSignTokenValues = Readonly<Partial<Record<DocuSignToken, string | null>>>;

/**
 * What each token degrades to when there is nothing to substitute.
 *
 * ⚠️ **ONLY `signerFirstName` DEGRADES TO A WORD, AND IT IS THE GREETING THAT
 * FORCES IT.** The other three sit in the middle of a sentence or at the end of
 * one, where a word would be nonsense — `SEED Quote there` — and dropping them
 * leaves prose that reads. A greeting is the opposite: `Hi ,` is not prose, and
 * there is no seam-tidying rule that turns `Hi {{signerFirstName}},` into
 * anything readable once the name is gone, because the fix is a WORD rather than
 * whitespace.
 *
 * ⚠️ **`there` rather than a rewritten greeting, and the difference matters the
 * moment an administrator edits the wording.** Swapping the whole `Hi …,`
 * construction for `Hello,` would work for exactly the shipped default and fail
 * for `Dear {{signerFirstName}},` or `{{signerFirstName}} — hello`, which this
 * feature exists to let somebody write. A per-token fallback composes with every
 * template there can be.
 *
 * Exhaustive over {@link DocuSignToken}, so a fifth token cannot be added
 * without a decision about what a customer reads when it is empty.
 */
const TOKEN_FALLBACKS: Record<DocuSignToken, string> = {
  signerFirstName: 'there',
  quoteNumber: '',
  companyName: '',
  senderName: '',
};

/**
 * A placeholder, together with the horizontal whitespace either side of it.
 *
 * ⚠️ **THE SURROUNDING SPACE IS CAPTURED ON PURPOSE** — it is what lets a
 * removed token close its own seam without a document-wide whitespace pass. A
 * global collapse would reformat an administrator's deliberate layout; this only
 * ever touches the few characters a placeholder was sitting in.
 *
 * ⚠️ **`[^\S\r\n]` is "whitespace that is not a line break".** Matching plain
 * `\s` would swallow the blank lines between paragraphs whenever a token sat at
 * the start or end of one, silently reflowing the message.
 *
 * The contents may not contain a brace or a line break, so `{{a}}` and
 * `{{ a }}` and `{{typo}}` all match while a stray `{{` with no partner on the
 * same line is left alone as the prose it is. See {@link fillTemplate} for why
 * that boundary is stated rather than closed.
 */
const PLACEHOLDER = /([^\S\r\n]*)\{\{([^{}\r\n]*)\}\}([^\S\r\n]*)/g;

/**
 * Fill a template. **No unresolved placeholder can survive this function.**
 *
 * ## 🔴 THE INVARIANT — this is what a test would assert
 *
 * ```
 * // For every template built from well-formed placeholders, and for EVERY
 * // values object including an empty one:
 * fillTemplate(template, values).includes('{{') === false
 *
 * // Specifically, and these are the cases that matter:
 * fillTemplate('Hi {{signerFirstName}},', {})            === 'Hi there,'
 * fillTemplate('Hi {{signerFirstName}},', { signerFirstName: null })
 *                                                        === 'Hi there,'
 * fillTemplate('SEED Quote {{quoteNumber}}', {})          === 'SEED Quote'
 * fillTemplate('the {{companyName}} account', {})         === 'the account'
 * fillTemplate('a {{notAToken}} b', {})                   === 'a b'
 * fillTemplate('Hi {{ signerFirstName }}!', { signerFirstName: 'Priya' })
 *                                                        === 'Hi Priya!'
 * ```
 *
 * ⚠️ **THE GATE IS THE SHAPE, NOT THE TOKEN LIST**, and that is the whole
 * guarantee. Replacing only the four known names would leave an administrator's
 * `{{invoiceNumber}}` to print raw in a customer's inbox; matching the shape and
 * emptying anything unresolvable means a typo costs a missing word rather than a
 * visible defect. An unknown name is therefore treated exactly as a known one
 * with nothing to substitute.
 *
 * ⚠️ **The one thing that survives, stated rather than hidden:** a `{{` with no
 * matching `}}` on the same line. That is not a placeholder, it is prose
 * carrying two braces, and removing arbitrary text on the strength of an opening
 * brace is a worse failure than leaving it. The invariant above is about
 * well-formed placeholders, which is what {@link tokenPlaceholder} produces and
 * what any screen listing the tokens will have offered.
 *
 * ## Closing the seam
 *
 * Two mechanisms, and neither alone is enough:
 *
 * 1. **A per-token fallback** ({@link TOKEN_FALLBACKS}) for the one token whose
 *    hole cannot be closed with whitespace — the greeting.
 * 2. **Seam-aware removal** for the rest. A placeholder that comes to nothing
 *    takes its surrounding horizontal space with it, and gives back a single
 *    space only when there was space on BOTH sides — so `the {{x}} account`
 *    becomes `the account` rather than `the  account`, and
 *    `SEED Quote {{x}}` at the end of a line becomes `SEED Quote` rather than
 *    leaving a trailing space before the newline.
 *
 * ⚠️ **What this deliberately does NOT do: remove a line that a token emptied.**
 * A template line holding nothing but `{{companyName}}` becomes a blank line.
 * That is invisible in every mail client, and deleting a line somebody typed is
 * a larger liberty than leaving one they will not see.
 *
 * ⚠️ **Values are trimmed for the emptiness test and substituted trimmed.** A
 * first name arriving as `'Priya '` must not produce `Hi Priya ,`.
 */
export function fillTemplate(template: string, values: DocuSignTokenValues): string {
  return template.replace(
    PLACEHOLDER,
    (_match: string, before: string, rawName: string, after: string) => {
      const filled = resolveTokenValue(rawName, values);
      if (filled !== '') return `${before}${filled}${after}`;

      // The placeholder came to nothing. Keep one separator where there were
      // words on both sides of it; otherwise take the whitespace with it, so a
      // dropped token leaves no trailing space and no double space behind.
      return before !== '' && after !== '' ? ' ' : '';
    },
  );
}

/**
 * What one placeholder resolves to, or `''` when it resolves to nothing at all.
 *
 * ⚠️ **The supplied value is tested POSITIVELY — `typeof === 'string'` and
 * non-empty — rather than against `null`.** The caller's object is
 * `Partial<…>`, so a missing key is `undefined`; a server or a record can supply
 * an empty string or whitespace; and a `?? fallback` would pass the last two
 * straight through into a customer's email. One test covers every absent shape
 * there is, including ones nobody anticipated.
 */
function resolveTokenValue(rawName: string, values: DocuSignTokenValues): string {
  const name = rawName.trim();
  // An unknown name is not an error and is not printed — it resolves to nothing,
  // exactly as a known token with no value does.
  if (!isDocuSignToken(name)) return '';

  const supplied = values[name];
  if (typeof supplied === 'string') {
    const trimmed = supplied.trim();
    if (trimmed !== '') return trimmed;
  }
  return TOKEN_FALLBACKS[name];
}
